/**
 * Ryan O'Dowd
 * 2022-03-30
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Platform,
  View,
} from 'react-native';
import React, {
  useState,
} from 'react';
import EmptyScreen from '@components/common/EmptyScreen';
import {
  FAB,
} from 'react-native-paper';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import GoalProgress from '@components/GoalProgress';
import Header from '@components/Header';
import {
  NEW_GOAL_ROUTE,
} from '@utils/store/actions';
import ScreenWrapper from '@containers/ScreenWrapper';
import Searchbar from '@components/common/Searchbar';
import SectionList from '@components/common/SectionList';
import Text from '@components/common/Text';
import WalkthroughTooltip from '@components/WalkthroughTooltip';
import WalkthroughTooltipContent from '@components/WalkthroughTooltip/Content';
import getStyles from './styles';
import hooks from '@hooks';

// @TODO: make this a section list by goal_type
const Goals = () => {
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  hooks.useBackButton(() => {
    return false;
  });

  const goalType = locationState.goalType || 'memory';

  const goals = hooks.useTypedSelector((state) => Object.values(state.goals).filter((goal: any) => goal.goal_type === goalType), (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const [_walkthroughVisible, setWalkthroughVisible] = useState(goals.length <= 1);
  const [_searchOpen, setSearchOpen] = useState(false);
  const [_searchText, setSearchText] = useState('');

  const searchItem = (
    <Header.Icon
      icon='magnify'
      onPress={() => {
        const newSearchOpenState = !_searchOpen;
        if (!newSearchOpenState) {
          setSearchText('');
        }
        setSearchOpen(newSearchOpenState);
      }}
    />
  );

  let goalSections: any = {}; // @TODO: allow these to be sorted alphabetically, by date, or by last-active // @TODO: this should be an array because that's how it's reassigned later
  goals.forEach((goal: any) => { // @TODO: differentiate between memory, reading, and group
    if (!_searchText || goal.title.toLowerCase().includes(_searchText.toLowerCase())) {
      if (!Object.keys(goalSections).includes(goal.user_goal.status)) {
        goalSections[goal.user_goal.status] = {
          key: goal.user_goal.status,
          title: goal.user_goal.status,
          data: [],
        };
      }
      goalSections[goal.user_goal.status].data.push(goal);
    }
  });
  goalSections = Object.values(goalSections).sort((a: any, b: any) => {
    // order should be `active`, `paused`, `complete`
    if (a.title === 'active') {
      return -1;
    } else if (a.title === 'paused' && b.title === 'complete') {
      return -1;
    }
    return 0;
  });

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        title={i18n('allGoalsWithType', goalType)}
        rightItems={[searchItem]}// @TODO: and sort by creation date, last-modified date, alphabetically?
      />
      {_searchOpen &&
        <Searchbar
          onChangeText={(searchText) => setSearchText(searchText)}
          value={_searchText}
          placeholder={i18n('goalName')}
        />
      }
      <FullScreenScrollView>
        <View style={styles.innerContainer}>
          {goalSections.length > 0
            ? (
              <SectionList
                contentContainerStyle={styles.list}
                sections={goalSections}
                renderItem={({item}) => {
                  const goalIsComplete = item.user_goal.status === 'complete';
                  const goalIsArchived = item.user_goal.status === 'archived';
                  return (
                    <GoalProgress
                      {...item}
                      hideRings={goalIsComplete || goalIsArchived}
                      hideEdit={goalIsComplete || goalIsArchived}
                      isComplete={goalIsComplete}
                    />
                  );
                }}
              />
            ) : (
              <EmptyScreen.Wrapper>
                <EmptyScreen.Text>
                  {_searchText
                    ? i18n('noResultsForSearch', _searchText)
                    : i18n('goalsEmptyScreen')
                  }
                </EmptyScreen.Text>
              </EmptyScreen.Wrapper>
            )
          }
        </View>
      </FullScreenScrollView>
      {goalType === 'memory' &&
        <View style={styles.tooltipWrapper}>
          <WalkthroughTooltip
            placement='top'
            isVisible={_walkthroughVisible}
            onClose={() => setWalkthroughVisible(false)}
            closeOnChildInteraction={true}
            closeOnContentInteraction={false}
            allowChildInteraction={true}
            topAdjustment={Platform.OS === 'android' ? -styles.safeAreaInsets.top : 0}// @TODO: from docs: Value which provides additional vertical offest for the child element displayed in a tooltip. Commonly set to: Platform.OS === 'android' ? -StatusBar.currentHeight : 0 due to an issue with React Native's measure function on Android // @TODO: think web needs adjustment here as well
            content={(
              <WalkthroughTooltipContent
                title={i18n('newGoalTitle')}
                content={(
                  <View style={styles.walkthroughContentRow}>
                    <Text style={styles.walkthroughText}>{i18n('newGoalBody')}</Text>
                  </View>
                )}
              />
            )}
          >
            <React.Fragment>
              {_walkthroughVisible && <View style={styles.fakeFab} />}
              <FAB
                style={styles.fab}
                icon='plus'
                color={styles.colors.primaryExtraDark}
                onPress={() => navigation.navigate(NEW_GOAL_ROUTE)}
              />
            </React.Fragment>
          </WalkthroughTooltip>
        </View>
      }
    </ScreenWrapper>
  );
};

export default Goals;
